import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cn, formatIST } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useIsMobile } from '@/hooks/useIsMobile';
import PaperLedger from './Inventory/PaperLedger';
import InkLedger from './Inventory/InkLedger';
import StockCards from './Inventory/StockCards';
import MovementSheet from './Inventory/MovementSheet';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Warehouse, Plus, AlertTriangle, ArrowUpCircle, ArrowDownCircle, Search, Pencil, RefreshCw, X, Scale, Droplet,
} from 'lucide-react';
import { validateField, formatApiError } from '@/lib/validators';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane, NumberCell, TextCell } from '@/components/data';

/**
 * Stock and its movements, together.
 *
 * These were two tabs. The question a store keeper actually asks is "why is this
 * number what it is", and answering it meant remembering the figure, switching
 * tab, and finding the item again in an unfiltered transaction list. Selecting
 * an item now fills the rail with that item's movements.
 *
 * Adjusting stock is a strip under the row rather than a dialog, and an item
 * below its minimum is a red number in the cell — which is what retires the
 * separate low-stock alert card that used to sit above the table.
 *
 * On a phone none of that geometry survives: an eight-column grid is a sideways
 * scroll, the rail has nowhere to sit, and the adjust strip opens inside a table
 * wider than the screen. Below lg the same state drives a different layout —
 * cards with in/out buttons on them, and a full-screen sheet for the movement —
 * so the store keeper can keep the day's stock from the floor rather than from a
 * desk. See StockCards and MovementSheet.
 *
 * The Paper (kg) and Ink (kg) ledgers keep their own views: weight-based stock
 * really is a different shape from unit stock, and it is the one split worth
 * keeping. Both read the same four columns because both are the same question.
 */

const CATEGORIES = ['Paper', 'Board', 'Ink', 'Plates', 'Lamination', 'Adhesive', 'Packaging', 'Consumables', 'Other'];
const UNITS = ['Kg', 'Sheets', 'Rolls', 'Litres', 'Pieces', 'Reams', 'Meters'];

const INK_TYPES = [
  ['offset', 'Offset'], ['uv', 'UV'], ['flexo', 'Flexo'],
  ['screen', 'Screen'], ['water_based', 'Water-based'], ['solvent', 'Solvent'],
];

const PAPER_FORMS = [['sheet', 'Sheet'], ['reel', 'Reel']];

// `form` is always set, so it cannot say whether a board was actually specified
const PAPER_IDENTITY = ['code', 'mill', 'brand', 'gsm', 'width_mm', 'length_mm', 'reel_no'];

const EMPTY_ITEM = {
  name: '', category: 'Paper', unit: 'Kg', current_stock: '',
  minimum_stock: '', location: '', cost_per_unit: '',
  paper: { code: '', mill: '', brand: '', gsm: '', form: 'sheet', width_mm: '', length_mm: '', reel_no: '' },
  ink: { brand: '', type: 'offset', shade: '', pantone: '', tin_kg: '', batch_no: '' },
};

const EMPTY_TXN = { type: 'stock_in', quantity: '', reference: '', remarks: '' };

export default function InventoryPage() {
  const isMobile = useIsMobile();

  const [view, setView] = useState('stock');
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [itemPane, setItemPane] = useState(null); // 'add' | item
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [submitting, setSubmitting] = useState(false);

  const [adjusting, setAdjusting] = useState(null);
  const [txnForm, setTxnForm] = useState(EMPTY_TXN);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (catFilter !== 'all') params.category = catFilter;
      if (showLowStock) params.low_stock = true;
      const [itemsRes, txnRes] = await Promise.all([
        api.get('/inventory', { params }),
        api.get('/inventory/transactions'),
      ]);
      setItems(itemsRes.data.items);
      setTotalItems(itemsRes.data.total);
      setLowStockCount(itemsRes.data.low_stock_count);
      setTransactions(txnRes.data.transactions);
    } catch { toast.error('Failed to load inventory'); }
    setLoading(false);
  }, [search, catFilter, showLowStock]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isInk = itemForm.category === 'Ink';
  // the store files board under either heading, and both belong on the kg ledger
  const isPaper = itemForm.category === 'Paper' || itemForm.category === 'Board';
  // a spec is what puts an item on the kg ledger, so a bare Paper item — a ream
  // of letterhead, a roll of tape filed under Paper — stays unit stock
  const hasPaperSpec = isPaper && PAPER_IDENTITY.some(k => String(itemForm.paper[k] || '').trim());
  const namedBySpec = itemPane === 'add' && (isInk || hasPaperSpec);
  const setInk = (key, value) => setItemForm(f => ({ ...f, ink: { ...f.ink, [key]: value } }));
  const setPaper = (key, value) => setItemForm(f => ({ ...f, paper: { ...f.paper, [key]: value } }));

  const saveItem = async () => {
    // an ink or a board names itself from its spec, so a blank name is only a
    // gap for everything else
    if (!itemForm.name && !namedBySpec) {
      toast.error('Name is required'); return;
    }
    setSubmitting(true);
    try {
      if (itemPane === 'add') {
        if (itemForm.current_stock && Number(itemForm.current_stock) < 0) {
          toast.error('Stock quantity must be 0 or more');
          setSubmitting(false);
          return;
        }
        const { ink, paper, ...base } = itemForm;
        await api.post('/inventory', {
          ...base,
          current_stock: parseFloat(itemForm.current_stock) || 0,
          minimum_stock: parseFloat(itemForm.minimum_stock) || 0,
          cost_per_unit: parseFloat(itemForm.cost_per_unit) || 0,
          // the spec is what puts an item on the kg ledger, so it only travels
          // for an ink — sending it always would make every screw an ink
          ...(isInk ? { ink: { ...ink, tin_kg: parseFloat(ink.tin_kg) || 0 } } : {}),
          ...(hasPaperSpec ? {
            paper: {
              ...paper,
              gsm: parseFloat(paper.gsm) || 0,
              width_mm: parseFloat(paper.width_mm) || 0,
              length_mm: parseFloat(paper.length_mm) || 0,
            },
          } : {}),
        });
        toast.success('Item added');
      } else {
        await api.put(`/inventory/${itemPane.id}`, {
          name: itemForm.name || undefined,
          category: itemForm.category || undefined,
          minimum_stock: itemForm.minimum_stock ? parseFloat(itemForm.minimum_stock) : undefined,
          location: itemForm.location || undefined,
          cost_per_unit: itemForm.cost_per_unit ? parseFloat(itemForm.cost_per_unit) : undefined,
        });
        toast.success('Item updated');
      }
      setItemPane(null);
      setItemForm(EMPTY_ITEM);
      fetchData();
    } catch (e) { toast.error(formatApiError(e, 'Failed to save item')); }
    setSubmitting(false);
  };

  const openAdd = () => { setItemForm(EMPTY_ITEM); setItemPane('add'); };
  const openEdit = (item) => {
    setItemForm({
      name: item.name, category: item.category, unit: item.unit, current_stock: '',
      minimum_stock: String(item.minimum_stock || ''), location: item.location || '',
      cost_per_unit: String(item.cost_per_unit || ''),
      paper: {
        ...EMPTY_ITEM.paper, ...(item.paper || {}),
        gsm: String(item.paper?.gsm || ''),
        width_mm: String(item.paper?.width_mm || ''),
        length_mm: String(item.paper?.length_mm || ''),
      },
      ink: { ...EMPTY_ITEM.ink, ...(item.ink || {}), tin_kg: String(item.ink?.tin_kg || '') },
    });
    setItemPane(item);
  };

  // the phone's two buttons choose the direction on the way in, so the sheet
  // opens on the one that was pressed rather than always on Stock In
  const startAdjust = (item, type = 'stock_in') => {
    setAdjusting(item.id);
    setSelectedId(item.id);
    setTxnForm({ ...EMPTY_TXN, type });
  };

  const submitAdjust = async (item) => {
    if (validateField('quantityPositive', txnForm.quantity)) { toast.error('Enter a valid quantity'); return; }
    setSubmitting(true);
    try {
      await api.post('/inventory/transactions', {
        item_id: item.id,
        type: txnForm.type,
        quantity: parseFloat(txnForm.quantity),
        reference: txnForm.reference,
        remarks: txnForm.remarks,
      });
      toast.success(`Stock ${txnForm.type === 'stock_in' ? 'added' : 'removed'}`);
      setAdjusting(null);
      fetchData();
    } catch (e) { toast.error(formatApiError(e, 'Failed to record transaction')); }
    setSubmitting(false);
  };

  const isLow = (item) => item.minimum_stock > 0 && item.current_stock <= item.minimum_stock;

  const renderAdjustStrip = (item) => {
    const qty = parseFloat(txnForm.quantity) || 0;
    const after = txnForm.type === 'stock_in' ? item.current_stock + qty : item.current_stock - qty;
    return (
      <div className="flex flex-wrap items-end gap-2">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-brand-blue">
          Adjust {item.name}
        </span>
        <label className="flex flex-col gap-0.5" style={{ width: '7rem' }}>
          <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">Direction</span>
          <Select value={txnForm.type} onValueChange={(v) => setTxnForm(f => ({ ...f, type: v }))}>
            <SelectTrigger className="h-6 text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="stock_in" className="text-xs">Stock In</SelectItem>
              <SelectItem value="stock_out" className="text-xs">Stock Out</SelectItem>
            </SelectContent>
          </Select>
        </label>
        {[['Quantity', 'quantity', 'number', '6rem'], ['Reference', 'reference', 'text', '9rem'], ['Remarks', 'remarks', 'text', '14rem']]
          .map(([label, key, type, width]) => (
            <label key={key} className="flex flex-col gap-0.5" style={{ width }}>
              <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
              <span className="rounded border border-slate-300 bg-white">
                {type === 'number' ? (
                  <NumberCell label={label} value={txnForm[key]} onChange={(v) => setTxnForm(f => ({ ...f, [key]: v }))} />
                ) : (
                  <TextCell label={label} value={txnForm[key]} onChange={(v) => setTxnForm(f => ({ ...f, [key]: v }))} />
                )}
              </span>
            </label>
          ))}
        <Button
          size="sm"
          className="h-7 bg-brand-blue text-white hover:bg-brand-blue-dark"
          disabled={submitting}
          onClick={() => submitAdjust(item)}
        >
          {submitting ? 'Saving…' : 'Record'}
        </Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => setAdjusting(null)}>
          <X className="h-3.5 w-3.5" />
        </Button>
        {qty > 0 && (
          <span className={`w-full text-[11px] ${after < 0 ? 'text-red-600' : after <= item.minimum_stock ? 'text-amber-700' : 'text-slate-500'}`}>
            {item.current_stock} → {after} {item.unit}
            {after < 0 ? ' — this would take stock negative.'
              : after <= item.minimum_stock ? ` — at or below the ${item.minimum_stock} minimum.` : ''}
          </span>
        )}
      </div>
    );
  };

  const rows = useMemo(() => {
    const out = [];
    for (const item of items) {
      out.push(item);
      // on a phone the movement is collected by MovementSheet instead — an inline
      // strip inside a table this wide opens off the side of the screen
      if (!isMobile && adjusting === item.id) {
        out.push({ id: `${item.id}:adjust`, __full: true, __render: () => renderAdjustStrip(item) });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, adjusting, txnForm, submitting, isMobile]);

  const columns = useMemo(() => ([
    {
      key: 'name',
      header: 'Item',
      width: 'minmax(13rem,1.6fr)',
      render: (item) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{item.name}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{item.unit}</span>
        </>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '8rem',
      render: (item) => <span className="block truncate text-[12px] text-slate-600">{item.category}</span>,
    },
    {
      key: 'current_stock',
      header: 'Stock',
      align: 'right',
      width: '7rem',
      render: (item) => (
        <span className={`font-semibold ${
          item.current_stock < 0 ? 'text-red-600' : isLow(item) ? 'text-amber-600' : 'text-slate-900'
        }`}>
          {item.current_stock?.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'minimum_stock',
      header: 'Minimum',
      align: 'right',
      width: '6.5rem',
      render: (item) => <span className="text-slate-500">{item.minimum_stock || '—'}</span>,
    },
    {
      key: 'cost_per_unit',
      header: 'Cost/unit',
      align: 'right',
      width: '7rem',
      render: (item) => <span className="text-slate-600">{item.cost_per_unit ? `₹${item.cost_per_unit.toLocaleString()}` : '—'}</span>,
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      width: '8rem',
      render: (item) => {
        const value = (item.current_stock || 0) * (item.cost_per_unit || 0);
        return <span className="font-medium text-slate-700">{value ? `₹${value.toLocaleString()}` : '—'}</span>;
      },
      total: (list) => {
        const sum = list
          .filter(r => !r.__full)
          .reduce((s, i) => s + (i.current_stock || 0) * (i.cost_per_unit || 0), 0);
        return sum ? `₹${sum.toLocaleString()}` : '';
      },
    },
    {
      key: 'location',
      header: 'Location',
      width: '8rem',
      render: (item) => <span className="block truncate text-[12px] text-slate-600">{item.location || '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '9rem',
      align: 'right',
      render: (item) => adjusting === item.id ? null : (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton
            icon={ArrowUpCircle}
            label="Stock in/out"
            onClick={() => startAdjust(item)}
            testId={`stock-txn-${item.id}`}
          />
          <CellButton
            icon={Pencil}
            label="Edit item"
            iconOnly
            onClick={() => openEdit(item)}
            testId={`edit-item-${item.id}`}
          />
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [adjusting]);

  const selectedItem = items.find(i => i.id === selectedId);
  const adjustItem = items.find(i => i.id === adjusting);
  const railTransactions = useMemo(
    () => (selectedItem ? transactions.filter(t => t.item_id === selectedItem.id) : transactions),
    [transactions, selectedItem]
  );

  const movementsBody = railTransactions.length === 0 ? (
    <p className="p-4 text-center text-xs text-slate-400">No transactions yet</p>
  ) : railTransactions.map((txn) => (
    <div key={txn.id} className="flex items-start justify-between gap-2 border-b border-slate-100 px-2.5 py-2.5 last:border-b-0 sm:py-1.5">
      <span className="min-w-0">
        <span className="flex items-center gap-1">
          {txn.type === 'stock_in'
            ? <ArrowUpCircle className="h-3 w-3 shrink-0 text-emerald-600" />
            : <ArrowDownCircle className="h-3 w-3 shrink-0 text-red-500" />}
          <span className="truncate text-[12px] font-medium text-slate-700">
            {selectedItem ? `${txn.stock_before} → ${txn.stock_after}` : txn.item_name}
          </span>
        </span>
        <span className="block truncate text-[10px] text-slate-400">
          {txn.recorded_by_name} · {txn.date ? formatIST(txn.date, { compact: true }) : '—'}
        </span>
        {txn.remarks && <span className="block truncate text-[10px] italic text-slate-400">{txn.remarks}</span>}
      </span>
      <span className={`shrink-0 text-[12px] font-bold tabular-nums ${
        txn.type === 'stock_in' ? 'text-emerald-600' : 'text-red-500'
      }`}>
        {txn.type === 'stock_in' ? '+' : '−'}{txn.quantity}
      </span>
    </div>
  ));

  const clearSelection = (
    <button
      type="button"
      onClick={() => setSelectedId(null)}
      aria-label="Show all movements"
      className="rounded p-1 text-white/80 hover:bg-white/20"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );

  const stats = [
    { key: null, label: 'items', value: totalItems, tone: 'blue' },
    { key: 'low', label: 'below minimum', value: lowStockCount, tone: lowStockCount ? 'danger' : 'good' },
  ];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  return (
    <Screen data-testid="inventory-page">
      <CommandBar
        title="Inventory"
        subtitle={`${totalItems} item${totalItems === 1 ? '' : 's'}`}
        actions={isMobile ? [
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchData },
          { key: 'add', label: 'Add', icon: Plus, tone: 'accent', onClick: openAdd, testId: 'add-item-btn' },
        ] : [
          { key: 'stock', label: 'Stock', icon: Warehouse, tone: view === 'stock' ? 'primary' : 'default', onClick: () => setView('stock') },
          { key: 'paper', label: 'Paper (kg)', icon: Scale, tone: view === 'paper' ? 'primary' : 'default', onClick: () => setView('paper') },
          { key: 'ink', label: 'Ink (kg)', icon: Droplet, tone: view === 'ink' ? 'primary' : 'default', onClick: () => setView('ink') },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchData },
          { key: 'add', label: 'Add Item', icon: Plus, tone: 'accent', onClick: openAdd, testId: 'add-item-btn' },
        ]}
      >
        {!isMobile && view === 'stock' && (
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="inventory-search"
              className="h-7 pl-8 text-xs"
              placeholder="Search name or paper code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
      </CommandBar>

      {/* the two views are CommandBar actions on a desktop; on a phone that bar
          is already three lines deep, so they become the tab strip instead */}
      {isMobile && (
        <div className="grid shrink-0 grid-cols-3 gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-card">
          {[['stock', 'Stock', Warehouse], ['paper', 'Paper (kg)', Scale], ['ink', 'Ink (kg)', Droplet]].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              aria-pressed={view === key}
              data-testid={`inventory-view-${key}`}
              className={cn(
                'flex h-10 items-center justify-center gap-1 rounded text-[13px] font-semibold transition-colors',
                view === key ? 'bg-brand-blue text-white shadow-sm' : 'text-slate-600 active:bg-slate-100'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          ))}
        </div>
      )}

      {view === 'paper' ? (
        <Pane title="Paper ledger (kg)" bodyClassName="p-3">
          <PaperLedger />
        </Pane>
      ) : view === 'ink' ? (
        <Pane title="Ink ledger (kg)" bodyClassName="p-3">
          <InkLedger />
        </Pane>
      ) : isMobile ? (
        <>
          <div className="relative shrink-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="inventory-search"
              className="h-12 border-slate-200 bg-white pl-9 pr-10 text-base shadow-card"
              placeholder="Search name or paper code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded text-slate-400 active:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <StatStrip
            stats={stats}
            activeKey={showLowStock ? 'low' : null}
            onSelect={(key) => setShowLowStock(key === 'low')}
            data-testid="inventory-stats"
          />

          {/* a Select hides eight of the nine categories behind a tap; the strip
              scrolls sideways and shows which one is on without opening anything */}
          <div className="-mx-3 flex shrink-0 gap-1.5 overflow-x-auto px-3 pb-0.5" data-testid="category-chips">
            {[['all', 'All'], ...CATEGORIES.map(c => [c, c])].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setCatFilter(value)}
                aria-pressed={catFilter === value}
                data-testid={`category-chip-${value}`}
                className={cn(
                  'h-9 shrink-0 whitespace-nowrap rounded-full border px-3.5 text-[13px] font-medium transition-colors',
                  catFilter === value
                    ? 'border-brand-blue bg-brand-blue text-white'
                    : 'border-slate-200 bg-white text-slate-600 active:bg-slate-100'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <StockCards
            items={items}
            isLow={isLow}
            selectedId={selectedId}
            onSelect={(item) => setSelectedId(item.id === selectedId ? null : item.id)}
            onStockIn={(item) => startAdjust(item, 'stock_in')}
            onStockOut={(item) => startAdjust(item, 'stock_out')}
            onEdit={openEdit}
          />

          {/* remounted on selection so picking an item opens its own history.
              The clear control sits in the body rather than the header: a
              collapsible Pane's header is itself a button, and a button cannot
              hold another one. */}
          <Pane
            key={selectedId || 'all'}
            title={selectedItem ? 'Movements' : 'Recent movements'}
            aside={selectedItem ? selectedItem.name : `${railTransactions.length}`}
            className="shrink-0"
            collapsible
            defaultOpen={!!selectedItem}
          >
            {selectedItem && (
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="flex h-11 w-full items-center justify-center gap-1.5 border-b border-slate-100 text-[13px] font-medium text-brand-blue active:bg-slate-50"
              >
                <X className="h-3.5 w-3.5" />
                Show all movements
              </button>
            )}
            {movementsBody}
          </Pane>
        </>
      ) : (
        <>
          <StatStrip
            stats={stats}
            activeKey={showLowStock ? 'low' : null}
            onSelect={(key) => setShowLowStock(key === 'low')}
            data-testid="inventory-stats"
          >
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Category</span>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="h-7 w-36 text-xs" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </StatStrip>

          <ScreenSplit
            railWidth="21rem"
            rail={
              <Pane
                title={selectedItem ? 'Movements' : 'Recent movements'}
                aside={selectedItem ? selectedItem.name : `${railTransactions.length}`}
                className="max-h-full"
                actions={selectedItem && clearSelection}
              >
                {movementsBody}
              </Pane>
            }
          >
            <Pane
              title="Stock"
              aside={showLowStock ? 'below minimum only' : catFilter !== 'all' ? catFilter : 'all categories'}
              scroll={false}
              bodyClassName="flex flex-col"
            >
              <DataGrid
                data-testid="inventory-table"
                columns={columns}
                rows={rows}
                getRowId={(item) => item.id}
                selectedId={selectedId}
                onRowClick={(item) => !item.__full && setSelectedId(item.id === selectedId ? null : item.id)}
                showTotals={items.length > 0}
                rowClassName={(item) => (!item.__full && isLow(item) ? 'bg-red-50/30' : '')}
                empty={
                  <span className="flex flex-col items-center gap-2 text-slate-400">
                    <AlertTriangle className="h-8 w-8 text-slate-300" />
                    No items found
                  </span>
                }
              />
            </Pane>
          </ScreenSplit>
        </>
      )}

      {isMobile && adjustItem && (
        <MovementSheet
          item={adjustItem}
          form={txnForm}
          onChange={setTxnForm}
          submitting={submitting}
          onSubmit={() => submitAdjust(adjustItem)}
          onClose={() => setAdjusting(null)}
        />
      )}

      <SidePane
        open={!!itemPane}
        onClose={() => { setItemPane(null); setItemForm(EMPTY_ITEM); }}
        overlay
        title={itemPane === 'add' ? 'Add inventory item' : `Edit ${itemPane?.name || 'item'}`}
        subtitle={itemPane === 'add' ? undefined : 'Stock is changed through a movement, not here'}
        width="26rem"
        footer={
          <>
            <Button variant="outline" className="h-12 px-4 text-sm sm:h-8"
              onClick={() => { setItemPane(null); setItemForm(EMPTY_ITEM); }}>
              Cancel
            </Button>
            <Button disabled={submitting} onClick={saveItem}
              className="h-12 flex-1 bg-brand-blue text-base font-semibold text-white hover:bg-brand-blue-dark sm:h-8 sm:flex-none sm:text-sm">
              {submitting ? 'Saving…' : itemPane === 'add' ? 'Add item' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">
              {namedBySpec ? 'Name' : 'Name *'}
            </span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={itemForm.name}
              placeholder={namedBySpec ? 'Left blank, the spec names it' : undefined}
              onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} />
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Category</span>
              <Select value={itemForm.category} onValueChange={v => setItemForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Unit</span>
              <Select value={itemForm.unit} onValueChange={v => setItemForm(f => ({ ...f, unit: v }))}
                disabled={itemPane !== 'add'}>
                <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map(u => <SelectItem key={u} value={u} className="text-xs">{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {itemPane === 'add' && (
              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Opening stock</span>
                <Input type="number" inputMode="decimal" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={itemForm.current_stock}
                  onChange={e => setItemForm(f => ({ ...f, current_stock: e.target.value }))} />
              </label>
            )}
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Minimum stock</span>
              <Input type="number" inputMode="decimal" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={itemForm.minimum_stock}
                onChange={e => setItemForm(f => ({ ...f, minimum_stock: e.target.value }))} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Cost per unit</span>
              <Input type="number" inputMode="decimal" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={itemForm.cost_per_unit}
                onChange={e => setItemForm(f => ({ ...f, cost_per_unit: e.target.value }))} />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Location</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="Rack / room" value={itemForm.location}
              onChange={e => setItemForm(f => ({ ...f, location: e.target.value }))} />
          </label>

          {/* the spec is what puts a board on the kg ledger — without it a paper
              item is just another line of unit stock. The code comes first: it
              is what the store looks the board up by, and it leads the built name */}
          {isPaper && itemPane === 'add' && (
            <div className="space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5"
              data-testid="paper-spec-fields">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-blue">Paper spec</p>
              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Paper Code</span>
                <Input className="mt-0.5 h-10 bg-white text-base uppercase sm:h-8 sm:text-sm" placeholder="PAP-320"
                  data-testid="paper-code"
                  value={itemForm.paper.code}
                  onChange={e => setPaper('code', e.target.value)} />
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Mill</span>
                  <Input className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm" placeholder="Century, JK…"
                    value={itemForm.paper.mill}
                    onChange={e => setPaper('mill', e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Brand</span>
                  <Input className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"
                    value={itemForm.paper.brand}
                    onChange={e => setPaper('brand', e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">GSM</span>
                  <Input type="number" inputMode="decimal" className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"
                    placeholder="320" value={itemForm.paper.gsm}
                    onChange={e => setPaper('gsm', e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Form</span>
                  <Select value={itemForm.paper.form} onValueChange={v => setPaper('form', v)}>
                    <SelectTrigger className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAPER_FORMS.map(([value, label]) => (
                        <SelectItem key={value} value={value} className="text-xs">{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Width (mm)</span>
                  <Input type="number" inputMode="decimal" className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"
                    placeholder="635" value={itemForm.paper.width_mm}
                    onChange={e => setPaper('width_mm', e.target.value)} />
                </label>
                {itemForm.paper.form === 'sheet' && (
                  <label className="block">
                    <span className="text-[11px] font-medium text-slate-600">Length (mm)</span>
                    <Input type="number" inputMode="decimal" className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"
                      placeholder="889" value={itemForm.paper.length_mm}
                      onChange={e => setPaper('length_mm', e.target.value)} />
                  </label>
                )}
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Reel no.</span>
                  <Input className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"
                    value={itemForm.paper.reel_no}
                    onChange={e => setPaper('reel_no', e.target.value)} />
                </label>
              </div>
            </div>
          )}

          {/* the spec is what puts a tin on the kg ledger — without it an ink is
              just another line of unit stock and the Ink (kg) view never sees it */}
          {isInk && itemPane === 'add' && (
            <div className="space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5"
              data-testid="ink-spec-fields">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-blue">Ink spec</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Brand</span>
                  <Input className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm" placeholder="Sicpa, Huber…"
                    value={itemForm.ink.brand}
                    onChange={e => setInk('brand', e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Press</span>
                  <Select value={itemForm.ink.type} onValueChange={v => setInk('type', v)}>
                    <SelectTrigger className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INK_TYPES.map(([value, label]) => (
                        <SelectItem key={value} value={value} className="text-xs">{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Pantone</span>
                  <Input className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm" placeholder="485 C"
                    value={itemForm.ink.pantone}
                    onChange={e => setInk('pantone', e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Shade</span>
                  <Input className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm" placeholder="Process Cyan"
                    value={itemForm.ink.shade}
                    onChange={e => setInk('shade', e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Tin size (kg)</span>
                  <Input type="number" inputMode="decimal" className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"
                    placeholder="2.5" value={itemForm.ink.tin_kg}
                    onChange={e => setInk('tin_kg', e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Batch no.</span>
                  <Input className="mt-0.5 h-10 bg-white text-base sm:h-8 sm:text-sm"
                    value={itemForm.ink.batch_no}
                    onChange={e => setInk('batch_no', e.target.value)} />
                </label>
              </div>
            </div>
          )}
        </div>
      </SidePane>
    </Screen>
  );
}
